import type { FeatureKey } from '@/api/auth';
import { computeHasFeature, useAuthStore } from '@/stores/auth';

/**
 * Reactive tariff-feature gate for components.
 *
 * Subscribes to the current user so the UI re-renders when the user (and thus
 * their features) changes. LOCKOUT-SAFE: returns true when features are not yet
 * loaded / absent, and always true for staff roles — see `computeHasFeature`.
 *
 * @example
 *   const canChat = useHasFeature('chat');
 *   if (!canChat) return <FeatureLocked />;
 */
export function useHasFeature(key: FeatureKey): boolean {
  const user = useAuthStore((s) => s.user);
  return computeHasFeature(user, key);
}

/**
 * Проверка сразу нескольких возможностей — одним подписчиком.
 *
 * ЗАЧЕМ. `useHasFeature` — хук, и вызвать его в цикле по списку карточек
 * нельзя: правила хуков запрещают условные и переменные по числу вызовы.
 * Из-за этого раздел тренажёров считал доступ ровно для двух ключей на
 * карточку, а третий молча игнорировался.
 *
 * Возвращает функцию-предикат, LOCKOUT-SAFE так же, как `useHasFeature`.
 */
export function useFeatureCheck(): (key: FeatureKey) => boolean {
  const user = useAuthStore((s) => s.user);
  return (key: FeatureKey) => computeHasFeature(user, key);
}
